import { breakpoints } from './breakpoints';
import { sizing } from './sizing';

/** Named container identifiers — use with `container-name` / Stitches `containerName`. */
export const containerNames = {
  application: 'application', // Treated almost as the viewport
  drawer: 'drawer',
  dialog: 'dialog',
  pane: 'pane',
  stage: 'stage',
};
export type ContainerName = (typeof containerNames)[keyof typeof containerNames];

/** Container `type` values that correspond to `containerNames` and `containerScales` entries. */
const containerTypes = containerNames;
export type ContainerType = 'pane' | 'drawer' | 'dialog';

/** Container bands: narrow, intermediate, wide. */
export const containerBands = ['narrow', 'intermediate', 'wide'];
export type ContainerBand = (typeof containerBands)[number];

export const containerValues = {
  drawer: sizing.primary * 2 + 320,
  dialog: 400,
  pane: 800,
};

const containerBasis = {
  // Based on the breakpoints for media queries`
  application: {
    narrow: breakpoints.width.small, // Mobile
    intermediate: breakpoints.width.medium, // Tablet
    wide: breakpoints.width.large, // Desktop
  },

  // Narrowest width `Drawer` continues to adjust until
  drawer: containerValues.drawer,

  // Narrowest width `Dialog` continues to adjust until
  dialog: containerValues.dialog,

  // Narrowest width `Pane` continues to adjust until
  pane: containerValues.pane,

  // Narrowest of pane + drawer
  stage: containerValues.pane + containerValues.drawer,
};

/** Pixel widths behind `containers.*` narrow / intermediate / wide bands (per named container). */
export const containerScales = {
  application: {
    narrow: containerBasis.application.narrow,
    intermediate: containerBasis.application.intermediate,
    wide: containerBasis.application.wide,
  },
  drawer: {
    narrow: containerBasis.drawer,
    intermediate: containerBasis.drawer * 2,
    wide: containerBasis.drawer * 3,
  },
  dialog: {
    narrow: containerBasis.dialog,
    intermediate: containerBasis.dialog * 2,
    wide: containerBasis.dialog * 3,
  },
  pane: {
    narrow: containerBasis.pane,
    intermediate: containerBasis.pane * 2,
    wide: containerBasis.pane * 3,
  },
  stage: {
    narrow: containerBasis.stage,
    intermediate: containerBasis.stage * 2,
    wide: containerBasis.stage * 3,
  },
};

/** Application uses three independent breakpoints (narrow / intermediate / wide). */
function containerQueriesApplication(
  name: string,
  basis: { narrow: number; intermediate: number; wide: number },
) {
  return {
    all: `@container ${name} (min-width: 0)`,
    narrow: `@container ${name} (max-width: ${basis.narrow}px)`,
    notNarrow: `@container ${name} (min-width: ${basis.narrow + 1}px)`,
    intermediate: `@container ${name} (min-width: ${basis.intermediate + 1}px) and (max-width: ${basis.wide}px)`,
    notWide: `@container ${name} (max-width: ${basis.wide}px)`,
    wide: `@container ${name} (min-width: ${basis.wide + 1}px)`,
  };
}

/**
 * Drawer / dialog / pane / stage: two thresholds (narrow, intermediate). The third scale
 * (`wide` in `containerScales`) is not used in these preludes — “wide” here means above intermediate.
 */
function containerQueriesTwoTier(name: string, narrow: number, intermediate: number) {
  return {
    all: `@container ${name} (min-width: 0)`,
    narrow: `@container ${name} (max-width: ${narrow}px)`,
    notNarrow: `@container ${name} (min-width: ${narrow + 1}px)`,
    intermediate: `@container ${name} (min-width: ${narrow + 1}px) and (max-width: ${intermediate}px)`,
    notWide: `@container ${name} (max-width: ${intermediate}px)`,
    wide: `@container ${name} (min-width: ${intermediate + 1}px)`,
  };
}

export const containers = {
  application: containerQueriesApplication(containerTypes.application, containerBasis.application),
  drawer: containerQueriesTwoTier(
    containerTypes.drawer,
    containerScales.drawer.narrow,
    containerScales.drawer.intermediate,
  ),
  dialog: containerQueriesTwoTier(
    containerTypes.dialog,
    containerScales.dialog.narrow,
    containerScales.dialog.intermediate,
  ),
  pane: containerQueriesTwoTier(
    containerTypes.pane,
    containerScales.pane.narrow,
    containerScales.pane.intermediate,
  ),
  stage: containerQueriesTwoTier(
    containerTypes.stage,
    containerScales.stage.narrow,
    containerScales.stage.intermediate,
  ),
};

/** Union of every predefined `@container` prelude — the only strings `containerOr` accepts. */
export type ContainerQuery = {
  [Family in keyof typeof containers]: (typeof containers)[Family][keyof (typeof containers)[Family]];
}[keyof typeof containers];

const CONTAINER_QUERY_VALUES = new Set<string>(
  (Object.keys(containers) as (keyof typeof containers)[]).flatMap((family) =>
    Object.values(containers[family]),
  ),
);

function assertKnownContainerQueries(queries: readonly string[]): void {
  for (const q of queries) {
    if (!CONTAINER_QUERY_VALUES.has(q)) {
      throw new Error(
        `[atto/containers] Unknown container query: ${JSON.stringify(
          q,
        )}. Use only values from \`containers\` (type \`ContainerQuery\`). Media queries belong in separate keys (e.g. \`[devices.touch]\`), not inside \`atContainers\`.`,
      );
    }
  }
}

/**
 * Apply the same styles under several container queries (OR semantics: any match applies).
 * CSS cannot OR different `@container` preludes in one rule; this expands to one key per query.
 *
 * Only preludes defined on `containers` are allowed (see type `ContainerQuery`). Do not pass
 * `@media` preludes — use separate object keys (e.g. `[devices.touch]: { … }` alongside `atContainers`).
 *
 * @example
 * atContainers([containers.pane.narrow, containers.drawer.all], { display: 'grid' })
 */
export function atContainers<Styles extends Record<string, unknown>>(
  queries: readonly ContainerQuery[],
  styles: Styles,
): Record<string, Styles> {
  assertKnownContainerQueries(queries);
  return Object.fromEntries(queries.map((q) => [q, styles])) as Record<string, Styles>;
}
